// Première connexion d'un compte Google inconnu de Kaiylo.
//
// Le backend (GET /auth/me) ne crée plus un coach sans le demander :
//  - 409 + code « student_… » quand la même boîte mail a déjà un compte élève ou
//    une invitation (adresse Gmail écrite avec ou sans points, par exemple) ;
//  - sinon `pendingCoachSignup`, et l'écran /complete-profile demande de
//    confirmer la création du compte coach.
// Dans le premier cas, et quand la personne renonce sur /complete-profile, on
// annule le compte que Supabase a créé au retour de Google et on la renvoie sur
// /login avec une explication. Celle-ci passe par sessionStorage plutôt que par
// l'URL : elle contient une adresse e-mail, et la déconnexion qui suit peut
// déclencher une seconde navigation vers /login qui ferait perdre un paramètre.
import axios from 'axios';
import { getApiBaseUrlWithApi } from '../config/api';

const NOTICE_KEY = 'kaiylo:login-notice';
const NOTICE_TTL_MS = 5 * 60 * 1000;

export const LOGIN_NOTICE_CODES = {
  STUDENT_ACCOUNT: 'student_account_exists',
  STUDENT_INVITATION: 'student_invitation_pending',
  SIGNUP_CANCELLED: 'coach_signup_cancelled',
};

const REFUSAL_CODES = [LOGIN_NOTICE_CODES.STUDENT_ACCOUNT, LOGIN_NOTICE_CODES.STUDENT_INVITATION];

/** `{ code, email }` si l'erreur axios est le refus 409 de GET /auth/me, sinon null. */
export function getOAuthRefusal(error) {
  const data = error?.response?.status === 409 ? error.response.data : null;
  if (!data || !REFUSAL_CODES.includes(data.code)) return null;
  return { code: data.code, email: data.studentEmail || null };
}

/** Supprime côté serveur le compte Google en attente. Ne lève jamais. */
export async function cancelPendingOAuthSignup(accessToken) {
  try {
    await axios.post(`${getApiBaseUrlWithApi()}/auth/cancel-oauth-signup`, null, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      timeout: 5000,
    });
    return true;
  } catch {
    return false;
  }
}

export function setLoginNotice(notice) {
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ ...notice, at: Date.now() }));
  } catch {
    // Stockage indisponible (navigation privée) : la page de connexion reste utilisable.
  }
}

export function readLoginNotice() {
  try {
    const notice = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || 'null');
    if (!notice?.code || Date.now() - notice.at > NOTICE_TTL_MS) return null;
    return notice;
  } catch {
    return null;
  }
}

/**
 * Vrai pendant les quelques secondes où un retour de Google vient d'être refusé
 * ou annulé : le compte est en cours de suppression, et un GET /auth/me parti
 * en parallèle peut revenir en 401. L'appelant ne doit alors pas reconstruire
 * un utilisateur à partir de la session Supabase.
 */
export function isOAuthSignupBeingCancelled() {
  const notice = readLoginNotice();
  return Boolean(notice) && Date.now() - notice.at < 30 * 1000;
}

export function clearLoginNotice() {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // Rien à nettoyer.
  }
}
